import React, { useState } from 'react';
import {
  Box, Container, Typography, TextField, Button,
  Grid, Stack, IconButton, useTheme, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { motion } from 'motion/react';
import { content } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useMagneticButton } from '../../hooks/useMagneticButton';
import { useParallax } from '../../hooks/useParallax';
import { useTiltEffect } from '../../hooks/useTiltEffect';

const MotionBox = motion.create(Box);

const Contact: React.FC = () => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { overline, title, description, info, form } = content.contact;
  const { footer: footerContent } = content;
  const { fadeUp, stagger } = useAnimationVariants();

  const leftParallax  = useParallax({ outputRange: [30, -30] });
  const rightParallax = useParallax({ outputRange: [-30, 30] });
  const magnetic      = useMagneticButton({ strength: 0.25 });

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending]   = useState(false);
  const [snack,   setSnack]     = useState<{ open: boolean; type: 'success' | 'error'; msg: string }>({ open: false, type: 'success', msg: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch('https://formsubmit.co/ajax/sajjadkhalid753@gmail.com', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name:    formData.name,
          email:   formData.email,
          subject: formData.subject,
          message: formData.message,
          _captcha: 'false',
        }),
      });
      if (res.ok) {
        setFormData({ name: '', email: '', subject: '', message: '' });
        setSnack({ open: true, type: 'success', msg: "Message sent! I'll reply within 24 hours." });
      } else {
        throw new Error('failed');
      }
    } catch {
      setSnack({ open: true, type: 'error', msg: 'Something went wrong. Email me directly at sajjadkhalid753@gmail.com' });
    } finally {
      setSending(false);
    }
  };

  const contactRows = [
    { icon: <PhoneOutlinedIcon />,      label: info.phone.label,    value: info.phone.value,    href: `tel:${info.phone.value}` },
    { icon: <EmailOutlinedIcon />,      label: info.email.label,    value: info.email.value,    href: `mailto:${info.email.value}` },
    { icon: <LocationOnOutlinedIcon />, label: info.location.label, value: info.location.value, href: undefined },
  ];

  return (
    <Box id="contact" sx={{ py: { xs: 10, md: 16 }, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
      {/* Accent orbs */}
      <Box sx={{ position: 'absolute', top: '-20%', right: '-10%', width: '40%', paddingBottom: '40%', borderRadius: '50%', background: `radial-gradient(circle, ${theme.palette.primary.main}0E 0%, transparent 70%)`, pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />
      <Box sx={{ position: 'absolute', bottom: '-15%', left: '-8%', width: '30%', paddingBottom: '30%', borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,211,153,0.10) 0%, transparent 70%)', pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      {/* Decorative quotation mark */}
      <Typography sx={{ position: 'absolute', top: '5%', right: '-2%', fontFamily: '"Space Grotesk", sans-serif', fontSize: { xs: '8rem', md: '14rem' }, fontWeight: 900, color: theme.palette.primary.main, opacity: 0.03, lineHeight: 1, userSelect: 'none', pointerEvents: 'none', zIndex: 0 }}>
        "
      </Typography>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="flex-start">

          {/* Left: Info */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box ref={leftParallax.ref}>
              <MotionBox style={{ y: leftParallax.y }}>
                <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}>

                  <MotionBox variants={fadeUp}>
                    <Typography variant="overline" sx={{ color: 'primary.main', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.14em', mb: 2, display: 'block' }}>
                      {overline}
                    </Typography>
                  </MotionBox>
                  <MotionBox variants={fadeUp}>
                    <Typography variant="h2" component="h2" sx={{ color: 'text.primary', mb: 2.5, lineHeight: 1.15, fontWeight: 700 }}>
                      {title}
                    </Typography>
                  </MotionBox>
                  <MotionBox variants={fadeUp}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 4.5 }}>
                      {description}
                    </Typography>
                  </MotionBox>

                  {/* Contact info items with tilt */}
                  <MotionBox variants={stagger} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 5 }}>
                    {contactRows.map((item) => (
                      <MotionBox key={item.label} variants={fadeUp}>
                        <ContactRow item={item} />
                      </MotionBox>
                    ))}
                  </MotionBox>

                  {/* Social links */}
                  <MotionBox variants={fadeUp}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      Find me on
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      {[
                        { href: footerContent.socialLinks.github,   icon: <GitHubIcon />,   label: 'GitHub' },
                        { href: footerContent.socialLinks.linkedin, icon: <LinkedInIcon />, label: 'LinkedIn' },
                      ].map(({ href, icon, label }) => (
                        <motion.div key={label} whileHover={{ scale: 1.1, y: -2 }}>
                          <IconButton href={href} target="_blank" rel="noopener noreferrer" aria-label={label} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: '10px', color: 'text.secondary', transition: 'all 220ms ease', '&:hover': { color: 'primary.main', borderColor: 'primary.main', bgcolor: `${theme.palette.primary.main}10`, boxShadow: `0 0 16px ${theme.palette.primary.main}30` } }}>
                            {icon}
                          </IconButton>
                        </motion.div>
                      ))}
                    </Stack>
                  </MotionBox>
                </MotionBox>
              </MotionBox>
            </Box>
          </Grid>

          {/* Right: Form */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Box ref={rightParallax.ref}>
              <MotionBox style={{ y: rightParallax.y }}>
                <MotionBox
                  initial={{ opacity: 0, x: 48 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Box
                    sx={{
                      p: { xs: 3, md: 4.5 }, borderRadius: '20px',
                      border: '1.5px solid transparent',
                      backgroundImage: `linear-gradient(${isDark ? '#0D1117' : '#FFFFFF'}, ${isDark ? '#0D1117' : '#FFFFFF'}), linear-gradient(135deg, ${theme.palette.primary.main}50, #a78bfa40, #34d39930)`,
                      backgroundOrigin: 'border-box',
                      backgroundClip: 'padding-box, border-box',
                      backdropFilter: 'blur(32px) saturate(200%)',
                      position: 'relative', overflow: 'hidden',
                      boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
                    }}
                  >
                    {/* Top gradient bar */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: `linear-gradient(90deg, ${theme.palette.primary.main}, #a78bfa, #34d399)` }} />

                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 3.5, fontSize: '1.25rem' }}>
                      Send a message
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                      <Stack spacing={2.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                          <TextField fullWidth label={form.name} name="name" value={formData.name} onChange={handleChange} variant="outlined" required inputProps={{ 'aria-label': 'Your name' }} />
                          <TextField fullWidth label={form.email} name="email" type="email" value={formData.email} onChange={handleChange} variant="outlined" required inputProps={{ 'aria-label': 'Your email' }} />
                        </Stack>
                        <TextField fullWidth label={form.subject} name="subject" value={formData.subject} onChange={handleChange} variant="outlined" required inputProps={{ 'aria-label': 'Subject' }} />
                        <TextField fullWidth label={form.message} name="message" value={formData.message} onChange={handleChange} variant="outlined" multiline rows={5} required inputProps={{ 'aria-label': 'Message' }} />

                        {/* Magnetic submit button */}
                        <Box
                          ref={magnetic.ref}
                          onMouseMove={magnetic.handleMouseMove}
                          onMouseLeave={magnetic.handleMouseLeave}
                          component={motion.div}
                          style={{ x: magnetic.x, y: magnetic.y }}
                        >
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={sending}
                            endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                            sx={{
                              py: 1.75, fontSize: '1rem', fontWeight: 600,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}, #a78bfa)`,
                              boxShadow: `0 0 32px ${theme.palette.primary.main}40`,
                              '&:hover': {
                                background: `linear-gradient(135deg, #a78bfa, ${theme.palette.primary.main})`,
                                boxShadow: `0 0 48px ${theme.palette.primary.main}60`,
                                transform: 'translateY(-2px)',
                              },
                              '&.Mui-disabled': { opacity: 0.7 },
                            }}
                          >
                            {sending ? 'Sending…' : form.btn}
                          </Button>
                        </Box>
                      </Stack>
                    </Box>

                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', opacity: 0.6 }}>
                      {form.note}
                    </Typography>
                  </Box>
                </MotionBox>
              </MotionBox>
            </Box>
          </Grid>

        </Grid>
      </Container>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.type}
          variant="filled"
          sx={{ borderRadius: '10px', fontFamily: '"Space Grotesk", sans-serif' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Contact row with subtle tilt
interface ContactRowProps {
  item: { icon: React.ReactNode; label: string; value: string; href?: string };
}

const ContactRow: React.FC<ContactRowProps> = ({ item }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tilt = useTiltEffect({ maxRotation: 3 });

  return (
    <Box
      ref={tilt.ref}
      onMouseMove={tilt.handleMouseMove}
      onMouseLeave={tilt.handleMouseLeave}
      component={motion.div}
      style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY } as any}
    >
      <Box
        component={item.href ? 'a' : 'div'}
        href={item.href}
        sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          p: 2, borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          textDecoration: 'none', cursor: item.href ? 'pointer' : 'default',
          transition: 'border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease',
          ...(item.href && {
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: `0 0 20px ${theme.palette.primary.main}25`,
              transform: 'translateX(6px)',
            },
          }),
        }}
      >
        <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: `${theme.palette.primary.main}14`, border: `1px solid ${theme.palette.primary.main}28`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main', flexShrink: 0 }}>
          {item.icon}
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.625rem', letterSpacing: '0.08em', display: 'block', textTransform: 'uppercase' }}>
            {item.label}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, fontSize: '0.9rem' }}>
            {item.value}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Contact;
